import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import MainLayout from "../sidebar/Sidebar.js";
import api from "../api/api.js";
import { useAuth } from "../context/AuthContext.js";
import { ClipboardList, FolderOpen, ChevronRight, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Dashboard() {
  const [location, setLocation] = useState("Dashboard");
  const { user } = useAuth();
  const navigate = useNavigate();

  const {
    data: projects = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["projects", user],
    queryFn: async () => {
      const response = await api.get("/projects/");
      console.log("Full response data:", response.data);
      const sortedProjects = response.data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      return sortedProjects;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 15 * 60 * 1000, // 15 minutes
  });

  const projectsLen = projects.length;

  return (
    <MainLayout location={location} setLocation={setLocation}>
      <div className="bg-gray-50 min-h-full py-4 px-6 text-gray-900 space-y-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-6 text-center font-medium">
              Failed to load projects: {error.message}
            </div>
          )}

          {/* Dashboard Widgets */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Current Projects Widget */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <ClipboardList className="w-5 h-5 text-gray-600" />
                </div>
                <span className="text-sm text-gray-900">Current Projects</span>
              </div>

              <div className="mt-2">
                {isLoading ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="text-sm">Loading...</span>
                  </div>
                ) : (
                  <>
                    <div className="text-3xl font-light text-gray-900">
                      {projectsLen}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      Active in your workspace
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Recent Projects Widget */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <FolderOpen className="w-5 h-5 text-gray-600" />
                </div>
                <span className="text-sm text-gray-900">Recent Projects</span>
              </div>
              {isLoading ? (
                <div className="flex items-center gap-2 text-gray-500 mt-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="text-sm">Loading...</span>
                </div>
              ) : projects.length === 0 ? (
                <p className="text-gray-500 text-sm mt-2">No projects found.</p>
              ) : (
                <ul className="space-y-2 mt-2">
                  {projects.slice(0, 3).map((project) => (
                    <li key={project.id} className="text-sm text-gray-700">
                      <span className="font-medium flex items-center gap-1">
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                        {project.name}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Quick Navigation Widget */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm text-gray-900">Quick Navigation</span>
              </div>
              <div className="space-y-2 mt-2">
                <button
                  onClick={() => navigate("/Estimate")}
                  className="w-full text-left px-3 py-2 rounded-md border border-gray-300 text-sm hover:bg-gray-50 transition shadow-sm"
                >
                  Go to Estimate
                </button>
                <button
                  onClick={() => navigate("/ProjectList")}
                  className="w-full text-left px-3 py-2 rounded-md border border-gray-300 text-sm hover:bg-gray-50 transition shadow-sm"
                >
                  View Project List
                </button>
                <button
                  onClick={() => navigate("/Tasks")}
                  className="w-full text-left px-3 py-2 rounded-md border border-gray-300 text-sm hover:bg-gray-50 transition shadow-sm"
                >
                  Go to Tasks
                </button>
              </div>
            </div>
          </div>

          {/* Chart/Stats Overview */}
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h2 className="text-xl font-medium mb-3 text-gray-900">Overview</h2>
            <p className="text-sm text-gray-500 mb-4">
              This area could be used for charts, statistics, summaries, etc.
            </p>
            <div className="mt-4 h-32 bg-gray-50 rounded-lg flex items-center justify-center text-gray-500 border border-gray-200">
              Placeholder
            </div>
          </div>
      </div>
    </MainLayout>
  );
}

// Old Version
// import { useState } from "react";
// import { useQuery } from "@tanstack/react-query";
// import MainLayout from "../sidebar/Sidebar.js";
// import api from "../api/api.js";
// import { useAuth } from "../context/AuthContext.js";
// import { ClipboardList, FolderOpen, ChevronRight, Loader2 } from "lucide-react";
// import { useNavigate } from "react-router-dom";

// export default function Dashboard() {
//   const [location, setLocation] = useState("Dashboard");
//   const { user } = useAuth();
//   const navigate = useNavigate();

//   const {
//     data: projects = [],
//     isLoading,
//     error,
//   } = useQuery({
//     queryKey: ["projects", user],
//     queryFn: async () => {
//       const response = await api.get("/projects/");
//       console.log("Full response data:", response.data);
//       const sortedProjects = response.data.sort(
//         (a, b) => new Date(b.created_at) - new Date(a.created_at)
//       );
//       return sortedProjects;
//     },
//     enabled: !!user,
//     staleTime: 5 * 60 * 1000, // 5 minutes
//     cacheTime: 15 * 60 * 1000, // 15 minutes
//   });

//   const projectsLen = projects.length;

//   return (
//     <MainLayout location={location} setLocation={setLocation}>
//       <div className="bg-gray-50 py-6 text-gray-900">
//         <div className="max-w-6xl mx-auto space-y-10 p-2">
//           {error && (
//             <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-6 text-center font-medium">
//               Failed to load projects: {error.message}
//             </div>
//           )}

//           {/* Dashboard Widgets */}
//           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
//             {/* Current Projects Widget */}
//             <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-200 md:hover:border-green-500 md:hover:shadow-lg transition-all duration-300">
//               <div className="flex items-start justify-between">
//                 <h2 className="text-lg font-semibold text-gray-700">
//                   Current Projects
//                 </h2>
//                 <div className="text-green-600 bg-green-100 p-2 rounded-full">
//                   <ClipboardList className="h-5 w-5" />
//                 </div>
//               </div>

//               <div className="mt-4">
//                 {isLoading ? (
//                   <div className="flex items-center gap-2 text-gray-500">
//                     <Loader2 className="h-5 w-5 animate-spin" />
//                     <span className="text-sm">Loading...</span>
//                   </div>
//                 ) : (
//                   <p className="text-4xl font-bold text-green-600">
//                     {projectsLen}
//                   </p>
//                 )}
//               </div>
//             </div>

//             {/* Recent Projects Widget 2 */}
//             <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-200 md:hover:border-green-500 md:hover:shadow-lg transition-all duration-300">
//               <div className="flex items-start justify-between">
//                 <h2 className="text-lg font-semibold text-gray-700 mb-4">
//                   Recent Projects
//                 </h2>
//                 <div className="text-green-600 bg-green-100 p-2 rounded-full">
//                   <FolderOpen className="h-5 w-5" />
//                 </div>
//               </div>
//               {isLoading ? (
//                 <div className="flex items-center gap-2 text-gray-500">
//                   <Loader2 className="h-5 w-5 animate-spin" />
//                   <span className="text-sm">Loading...</span>
//                 </div>
//               ) : projects.length === 0 ? (
//                 <p className="text-gray-500 text-sm">No projects found.</p>
//               ) : (
//                 <ul className="space-y-2">
//                   {projects.slice(0, 3).map((project) => (
//                     <li key={project.id} className="text-sm text-gray-700">
//                       <span className="font-medium flex items-center gap-1">
//                         <ChevronRight className="w-4 h-4 text-gray-400" />
//                         {project.name}
//                       </span>
//                       <br />
//                     </li>
//                   ))}
//                 </ul>
//               )}
//             </div>

//             {/* Quick Navigation Widget 3 */}
//             <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-200 md:hover:border-green-500 md:hover:shadow-lg transition-all duration-300">
//               <h2 className="text-lg font-semibold mb-4 text-gray-700">
//                 Quick Navigation
//               </h2>
//               <div className="space-y-3">
//                 <button
//                   onClick={() => navigate("/Estimate")}
//                   className="w-full text-left px-4 py-2 rounded-lg border border-gray-300 hover:bg-green-100 transition"
//                 >
//                   Go to Estimate
//                 </button>
//                 <button
//                   onClick={() => navigate("/ProjectList")}
//                   className="w-full text-left px-4 py-2 rounded-lg border border-gray-300 hover:bg-green-100 transition"
//                 >
//                   View Project List
//                 </button>
//                 <button
//                   onClick={() => navigate("/Tasks")}
//                   className="w-full text-left px-4 py-2 rounded-lg border border-gray-300 hover:bg-green-100 transition"
//                 >
//                   Go to Tasks
//                 </button>
//               </div>
//             </div>
//           </div>

//           {/* Chart/Stats Overview */}
//           <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-200">
//             <h2 className="text-2xl font-semibold mb-4">Overview</h2>
//             <p className="text-gray-600">
//               This area could be used for charts, statistics, summaries, etc.
//             </p>
//             <div className="mt-6 h-32 bg-gray-100 rounded-xl flex items-center justify-center text-gray-500">
//               Placeholder
//             </div>
//           </div>
//         </div>
//       </div>
//     </MainLayout>
//   );
// }
